<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  writePreferredLocale,
  type AppLocale,
} from "@/library/i18n";
import { DEFAULT_LOCALE, pathFor, type Locale, type RouteName } from "@/seo/routes-manifest";
import { SIGNUP_URL } from "@/seo/config";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const currentLocale = computed<Locale>(
  () => (route.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE,
);

const currentRouteName = computed<RouteName>(
  () => (route.meta?.routeName as RouteName | undefined) ?? "home",
);

const homePath = computed(() => pathFor("home", currentLocale.value));
const philosophyPath = computed(() => pathFor("philosophy", currentLocale.value));

const currentLocaleLabel = computed(() => LOCALE_LABELS[locale.value as AppLocale]);

const localeItems = computed(() =>
  SUPPORTED_LOCALES.map((code) => ({
    label: LOCALE_LABELS[code],
    code,
    active: code === locale.value,
  })),
);

function switchLocale(code: AppLocale) {
  writePreferredLocale(code);
  router.push(pathFor(currentRouteName.value, code));
}

const hasScrolled = ref(false);
const mobileMenuOpen = ref(false);

function onScroll() {
  hasScrolled.value = window.scrollY > 50;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
});

const navLinks = computed(() => [
  { label: t("nav.howItWorks"), to: { path: homePath.value, hash: "#problem" } },
  { label: t("nav.themes"), to: { path: homePath.value, hash: "#themes" } },
  { label: t("nav.philosophy"), to: philosophyPath.value },
  { label: t("nav.blog"), to: "/blog" },
  { label: t("nav.pricing"), to: { path: homePath.value, hash: "#pricing" } },
]);
</script>

<template>
  <header
    class="sticky top-0 z-50 transition-shadow duration-200"
    :class="hasScrolled ? 'shadow-[0_2px_8px_rgba(31,29,27,0.06)]' : ''"
    :style="{
      backgroundColor: 'rgba(253, 250, 242, 0.95)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }"
  >
    <nav
      class="mx-auto flex h-16 max-w-[var(--max-w-page)] items-center justify-between px-5 md:px-12"
    >
      <!-- Logo -->
      <router-link :to="homePath" class="flex items-center gap-2">
        <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" class="size-8">
          <line
            x1="22"
            y1="38"
            x2="22"
            y2="18"
            stroke="#2EA84F"
            stroke-width="2.5"
            stroke-linecap="round"
          />
          <path d="M22 26 C22 26 14 24 12 16 C12 16 20 14 22 22" fill="#2EA84F" opacity="0.7" />
          <path d="M22 22 C22 22 30 20 32 12 C32 12 24 10 22 18" fill="#52CC6A" />
          <circle cx="22" cy="38" r="2.5" fill="#1A7A35" />
        </svg>
        <span
          class="text-xl font-medium tracking-tight text-[var(--color-text-primary)]"
          style="font-family: var(--font-display); letter-spacing: -0.5px"
          >cresci</span
        >
      </router-link>

      <!-- Desktop nav links -->
      <div class="hidden items-center gap-8 md:flex">
        <router-link
          v-for="link in navLinks"
          :key="link.label"
          :to="link.to"
          class="text-[15px] font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
        >
          {{ link.label }}
        </router-link>
        <!-- Language switcher -->
        <UPopover
          :content="{ align: 'end', sideOffset: 8 }"
          :ui="{
            content:
              'bg-[var(--color-surface)] border border-[var(--color-border)] shadow-[var(--shadow-card)]',
          }"
        >
          <button class="locale-trigger">
            <UIcon name="i-lucide-globe" class="size-4" />
            {{ currentLocaleLabel }}
            <UIcon name="i-lucide-chevron-down" class="size-3 opacity-50" />
          </button>
          <template #content>
            <div class="locale-dropdown">
              <button
                v-for="item in localeItems"
                :key="item.code"
                :class="['locale-dropdown__item', { 'is-active': item.active }]"
                @click="switchLocale(item.code)"
              >
                <UIcon
                  name="i-lucide-check"
                  class="locale-dropdown__check"
                  :class="item.active ? 'opacity-100' : 'opacity-0'"
                />
                {{ item.label }}
              </button>
            </div>
          </template>
        </UPopover>

        <a
          :href="SIGNUP_URL"
          class="inline-flex items-center rounded-full bg-[var(--color-primary)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--color-primary-hover)]"
        >
          {{ t("nav.getStarted") }}
        </a>
      </div>

      <!-- Mobile hamburger -->
      <button
        class="flex h-11 w-11 items-center justify-center md:hidden"
        :aria-label="t('nav.openMenu')"
        @click="mobileMenuOpen = true"
      >
        <UIcon name="i-lucide-menu" class="size-6 text-[var(--color-text-primary)]" />
      </button>
    </nav>

    <!-- Mobile drawer overlay -->
    <Teleport to="body">
      <Transition name="overlay">
        <div
          v-if="mobileMenuOpen"
          class="fixed inset-0 z-50 bg-black/30"
          @click="closeMobileMenu"
        />
      </Transition>

      <!-- Mobile drawer -->
      <Transition name="drawer">
        <div
          v-if="mobileMenuOpen"
          class="fixed top-0 right-0 z-50 flex h-full w-70 flex-col bg-[var(--color-bg-primary)] shadow-[-4px_0_16px_rgba(31,29,27,0.1)]"
        >
          <div class="flex h-16 items-center justify-end px-5">
            <button
              class="flex h-11 w-11 items-center justify-center"
              :aria-label="t('nav.closeMenu')"
              @click="closeMobileMenu"
            >
              <UIcon name="i-lucide-x" class="size-6 text-[var(--color-text-primary)]" />
            </button>
          </div>
          <div class="flex flex-1 flex-col gap-2 px-6 pt-4">
            <router-link
              v-for="link in navLinks"
              :key="link.label"
              :to="link.to"
              class="rounded-lg px-4 py-3 text-base font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
              @click="closeMobileMenu"
            >
              {{ link.label }}
            </router-link>
          </div>
          <!-- Mobile language switcher -->
          <div class="border-t border-[var(--color-border)] px-6 py-4">
            <p
              class="mb-3 text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)]"
            >
              <UIcon name="i-lucide-globe" class="mr-1 inline size-3.5 align-[-2px]" />
              {{ currentLocaleLabel }}
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="item in localeItems"
                :key="item.code"
                :class="[
                  'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                  item.active
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]',
                ]"
                @click="switchLocale(item.code)"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
          <div class="p-6 pt-0">
            <a
              :href="SIGNUP_URL"
              class="flex w-full items-center justify-center rounded-full bg-[var(--color-primary)] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[var(--color-primary-hover)]"
              @click="closeMobileMenu"
            >
              {{ t("nav.getStarted") }}
            </a>
          </div>
        </div>
      </Transition>
    </Teleport>
  </header>
</template>

<style scoped>
/* Overlay transitions */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 200ms ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Drawer transitions */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 300ms var(--ease-smooth);
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}

/* Desktop locale trigger */
.locale-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  transition: color 150ms ease;
}

.locale-trigger:hover {
  color: var(--color-text-primary);
}

/* Desktop locale dropdown */
.locale-dropdown {
  display: flex;
  flex-direction: column;
  padding: 4px;
}

.locale-dropdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px 8px 10px;
  border: none;
  background: none;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-secondary);
  cursor: pointer;
  text-align: left;
  transition:
    background 150ms ease,
    color 150ms ease;
}

.locale-dropdown__item:hover {
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

.locale-dropdown__item.is-active {
  font-weight: 500;
  color: var(--color-primary);
}

.locale-dropdown__check {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--color-primary);
  transition: opacity 150ms ease;
}
</style>
